import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 720,
});

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  flexWrap: "wrap",
  marginBottom: theme.spacing(5),
}));

const Card = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  display: "flex",
  flexDirection: "column",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { fontSize: 22 },
});

const CardHeading = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  marginBottom: 16,
  flex: 1,
});

const TechRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const TechChip = styled("span")({
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#1d648c",
  padding: "3px 9px",
  borderRadius: 6,
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  border: "1px solid rgba(47, 129, 173, 0.18)",
});

const OutlineButton = styled(Button)({
  padding: "9px 20px",
  borderRadius: 6,
  fontWeight: 600,
  fontSize: "0.88rem",
  textTransform: "none",
  color: "#0f172a",
  border: "1px solid rgba(15, 23, 42, 0.18)",
  backgroundColor: "transparent",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#0f172a",
    color: "#fff",
    borderColor: "#0f172a",
  },
  transition: "all 0.2s ease",
});

interface TechPlatform {
  icon: React.ReactNode;
  title: string;
  description: string;
  tools: string[];
}

const PLATFORMS: TechPlatform[] = [
  {
    icon: <CodeOutlinedIcon />,
    title: "Web development",
    description:
      "Fast, reliable web applications — from marketing sites to complex SaaS dashboards — with smooth performance and easy scaling.",
    tools: ["React", "Next.js", "TypeScript", "Vue", "Angular"],
  },
  {
    icon: <SmartphoneOutlinedIcon />,
    title: "Mobile development",
    description:
      "High-performance mobile apps for iOS and Android. Native or cross-platform — every app is fast, secure, and user-friendly.",
    tools: ["React Native", "Flutter", "Swift", "Kotlin"],
  },
  {
    icon: <StorageOutlinedIcon />,
    title: "Backend & APIs",
    description:
      "Robust backends, APIs, and data pipelines designed for reliability under load with full observability and security controls.",
    tools: ["Node.js", "Python", "Go", "PostgreSQL", "MongoDB"],
  },
  {
    icon: <CloudOutlinedIcon />,
    title: "Cloud development",
    description:
      "Cloud-native applications on AWS, GCP, and Azure with full lifecycle support to help you launch faster and adapt quickly.",
    tools: ["AWS", "GCP", "Azure", "Docker", "Kubernetes"],
  },
  {
    icon: <PsychologyOutlinedIcon />,
    title: "ML & AI development",
    description:
      "Machine learning and LLM-powered features that turn data into insights, automate workflows, and fit your product naturally.",
    tools: ["OpenAI", "LangChain", "TensorFlow", "PyTorch"],
  },
  {
    icon: <HubOutlinedIcon />,
    title: "DevOps & automation",
    description:
      "CI/CD pipelines, infrastructure as code, monitoring, and on-call setups so your team ships faster with fewer incidents.",
    tools: ["Terraform", "GitHub Actions", "Datadog", "Sentry"],
  },
];

const TechnologiesSection: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="tech-heading"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: "#f8fafc",
      }}
    >
      <Container maxWidth="lg">
        <HeaderRow
          sx={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Box>
            <Label>Technologies</Label>
            <SectionTitle id="tech-heading" variant="h2">
              The technology platforms we use
            </SectionTitle>
            <Lead>
              Our teams use the right technology for each task — no one-size-
              fits-all, buzzwords, or overengineering. We focus on long-term
              efficiency and making your product future-proof.
            </Lead>
          </Box>
          <OutlineButton
            onClick={() => navigate("/Technologies")}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          >
            All technologies
          </OutlineButton>
        </HeaderRow>

        <Grid container spacing={2.5}>
          {PLATFORMS.map((platform, i) => (
            <Grid item xs={12} sm={6} md={4} key={platform.title}>
              <Box
                sx={{
                  height: "100%",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 0.45s ease ${
                    0.05 + i * 0.05
                  }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                }}
              >
                <Card>
                  <IconBadge>{platform.icon}</IconBadge>
                  <CardHeading variant="h3">{platform.title}</CardHeading>
                  <CardCopy>{platform.description}</CardCopy>
                  <TechRow>
                    {platform.tools.map((t) => (
                      <TechChip key={t}>{t}</TechChip>
                    ))}
                  </TechRow>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TechnologiesSection;
